/* ==========================================================================
   BAAGHI — product.js
   Variant selection · media sync · gallery slider · sticky ATC · quick add
   ========================================================================== */
(function () {
  'use strict';

  const strings = (window.BAAGHI && window.BAAGHI.strings) || {};

  /* ── Variant picker ───────────────────────────────────────────────────── */

  function initVariantPicker(scope) {
    scope.querySelectorAll('[data-variant-picker]').forEach((picker) => {
      if (picker.dataset.bound === 'true') return;
      picker.dataset.bound = 'true';

      const productEl = picker.closest('[data-product-root]');
      if (!productEl) return;

      const dataEl = productEl.querySelector('[data-variant-json]');
      if (!dataEl) return;

      let variants = [];
      try {
        variants = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }

      const form = productEl.querySelector('form[data-product-form]');
      const idInput = form && form.querySelector('[data-variant-id]');
      const priceNode = productEl.querySelector('[data-price-wrapper]');
      const submit = form && form.querySelector('[type="submit"]');
      const label = submit && submit.querySelector('[data-button-label]');
      const stockNode = productEl.querySelector('[data-stock]');
      const skuNode = productEl.querySelector('[data-sku]');
      const updateUrl = picker.dataset.updateUrl === 'true';

      function selectedOptions() {
        return Array.from(picker.querySelectorAll('[data-option-index]')).map((group) => {
          const checked = group.querySelector('input:checked');
          const select = group.querySelector('select');
          return checked ? checked.value : select ? select.value : null;
        });
      }

      function matchVariant(options) {
        return variants.find((variant) => variant.options.every((opt, i) => opt === options[i]));
      }

      function updateAvailability(options) {
        picker.querySelectorAll('[data-option-index]').forEach((group) => {
          const index = parseInt(group.dataset.optionIndex, 10);
          group.querySelectorAll('input').forEach((input) => {
            const candidate = options.slice();
            candidate[index] = input.value;
            const exists = variants.some(
              (variant) =>
                variant.available &&
                variant.options.every((opt, i) => (i === index ? opt === input.value : opt === candidate[i] || candidate[i] === null))
            );
            input.disabled = !exists;
          });
        });
      }

      function updateMedia(variant) {
        if (!variant || !variant.featured_media) return;
        const gallery = productEl.querySelector('[data-gallery]');
        if (!gallery) return;
        const target = gallery.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
        if (!target) return;

        if (gallery.classList.contains('product__slides')) {
          gallery.scrollTo({ left: target.offsetLeft - gallery.offsetLeft, behavior: 'smooth' });
        } else {
          gallery.prepend(target);
        }
      }

      function update() {
        const options = selectedOptions();
        updateAvailability(options);
        const variant = matchVariant(options);

        picker.querySelectorAll('[data-option-selected]').forEach((node) => {
          const index = parseInt(node.dataset.optionSelected, 10);
          node.textContent = options[index] || '';
        });

        const finishNode = picker.querySelector('[data-option-finish]');
        if (finishNode) finishNode.textContent = variant && variant.finish ? variant.finish : '';

        if (!variant) {
          if (submit) {
            submit.setAttribute('aria-disabled', 'true');
            submit.disabled = true;
          }
          if (label) label.textContent = strings.unavailable || 'Unavailable';
          productEl.dispatchEvent(new CustomEvent('baaghi:variant:change', { detail: { variant: null } }));
          return;
        }

        if (idInput) idInput.value = variant.id;

        if (submit) {
          submit.disabled = !variant.available;
          submit.toggleAttribute('aria-disabled', !variant.available);
        }
        if (label) label.textContent = variant.available ? strings.addToCart : strings.soldOut;

        if (priceNode && variant.price_html) priceNode.innerHTML = variant.price_html;

        if (stockNode) {
          stockNode.hidden = false;
          stockNode.classList.remove('product__stock--low', 'product__stock--out');
          if (!variant.available) {
            stockNode.classList.add('product__stock--out');
            stockNode.textContent = strings.soldOut;
          } else if (variant.inventory_management && variant.inventory_quantity > 0 && variant.inventory_quantity <= 5) {
            stockNode.classList.add('product__stock--low');
            stockNode.textContent = `Low stock — ${variant.inventory_quantity} left`;
          } else {
            stockNode.textContent = 'In stock';
          }
        }

        if (skuNode) skuNode.textContent = variant.sku || '';

        updateMedia(variant);

        if (updateUrl && variant.id) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }

        productEl.dispatchEvent(new CustomEvent('baaghi:variant:change', { detail: { variant } }));
      }

      picker.addEventListener('change', update);
      update();
    });
  }

  /* ── Order on WhatsApp ──────────────────────────────────────────────── */

  function initWhatsAppOrders(scope) {
    scope.querySelectorAll('[data-whatsapp-product]').forEach((button) => {
      if (button.dataset.bound === 'true') return;
      button.dataset.bound = 'true';

      const productEl = button.closest('[data-product-root]');
      const dataEl = productEl && productEl.querySelector('[data-variant-json]');
      const idInput = productEl && productEl.querySelector('[data-variant-id]');
      const quantityInput = productEl && productEl.querySelector('input[name="quantity"]');
      if (!productEl || !dataEl || !idInput) return;

      let variants = [];
      try {
        variants = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }

      const optionNames = (button.dataset.optionNames || '').split('|');
      let currentVariant = variants.find((variant) => String(variant.id) === idInput.value) || null;

      function updateLink() {
        if (!currentVariant || !currentVariant.available) return;

        const quantity = Math.max(1, parseInt(quantityInput && quantityInput.value, 10) || 1);
        const productUrl = new URL(button.dataset.productUrl || window.location.href, window.location.origin);
        productUrl.searchParams.set('variant', currentVariant.id);

        const lines = [button.dataset.whatsappIntro || 'Hi BAAGHI, I would like to order:', '', `Product: ${button.dataset.productTitle}`];
        currentVariant.options.forEach((value, index) => {
          if (value && value !== 'Default Title') lines.push(`${optionNames[index] || `Option ${index + 1}`}: ${value}`);
        });
        lines.push(`Quantity: ${quantity}`);
        if (currentVariant.price_text) lines.push(`Price: ${currentVariant.price_text}`);
        lines.push(`Product link: ${productUrl.toString()}`);

        button.href = `https://wa.me/${button.dataset.whatsappNumber}?text=${encodeURIComponent(lines.join('\n'))}`;
      }

      function setVariant(variant) {
        currentVariant = variant;
        const unavailable = !variant || !variant.available;
        if (unavailable) {
          button.setAttribute('aria-disabled', 'true');
          button.setAttribute('tabindex', '-1');
          button.removeAttribute('href');
          return;
        }

        button.removeAttribute('aria-disabled');
        button.removeAttribute('tabindex');
        updateLink();
      }

      productEl.addEventListener('baaghi:variant:change', (event) => setVariant(event.detail.variant));
      if (quantityInput) {
        quantityInput.addEventListener('input', updateLink);
        quantityInput.addEventListener('change', updateLink);
      }
      button.addEventListener('click', (event) => {
        if (!currentVariant || !currentVariant.available) {
          event.preventDefault();
          return;
        }
        updateLink();
      });

      setVariant(currentVariant);
    });
  }

  /* ── Gallery slider: dots, thumbnails, arrows, keyboard ──────────────── */

  function initSliders(scope) {
    scope.querySelectorAll('[data-slider]').forEach((slider) => {
      if (slider.dataset.bound === 'true') return;
      slider.dataset.bound = 'true';
      const track = slider.querySelector('.product__slides');
      if (!track) return;
      const slides = Array.from(track.children);
      const dots = slider.querySelectorAll('[data-slide-dot]');
      const thumbs = slider.querySelectorAll('[data-slide-thumb]');
      const prev = slider.querySelector('[data-slide-prev]');
      const next = slider.querySelector('[data-slide-next]');
      if (slides.length < 2) return;

      const offsetOf = (i) => slides[i].offsetLeft - slides[0].offsetLeft;
      const current = () => {
        let best = 0;
        slides.forEach((slide, i) => {
          if (Math.abs(offsetOf(i) - track.scrollLeft) < Math.abs(offsetOf(best) - track.scrollLeft)) best = i;
        });
        return best;
      };
      const goTo = (i) => {
        const index = Math.max(0, Math.min(slides.length - 1, i));
        track.scrollTo({ left: offsetOf(index), behavior: 'smooth' });
      };

      function sync() {
        const index = current();
        dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
        thumbs.forEach((thumb, i) => {
          thumb.classList.toggle('is-active', i === index);
          thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
        if (prev) prev.disabled = index === 0;
        if (next) next.disabled = index === slides.length - 1;
      }

      track.addEventListener('scroll', sync, { passive: true });
      dots.forEach((dot, i) => dot.addEventListener('click', () => goTo(i)));
      thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => goTo(i)));
      if (prev) prev.addEventListener('click', () => goTo(current() - 1));
      if (next) next.addEventListener('click', () => goTo(current() + 1));
      track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); goTo(current() - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); goTo(current() + 1); }
      });
      slider.baaghiGoTo = goTo;
      sync();
    });
  }

  /* ── Zoom view (full-screen dialog; click/tap zooms, pointer pans) ─────── */

  function initZoom(scope) {
    scope.querySelectorAll('[data-zoom-dialog]').forEach((dialog) => {
      if (dialog.dataset.bound === 'true') return;
      dialog.dataset.bound = 'true';
      const root = dialog.closest('[data-product-root]') || document;
      const slider = root.querySelector('[data-slider]');
      const slides = Array.from(dialog.querySelectorAll('[data-zoom-slide]'));
      const count = dialog.querySelector('[data-zoom-count]');
      let index = 0;
      let startX = null;

      function show(i) {
        index = (i + slides.length) % slides.length;
        slides.forEach((slide, n) => {
          slide.hidden = n !== index;
          slide.classList.remove('is-zoomed');
        });
        const img = slides[index].querySelector('img');
        if (img && !img.getAttribute('src')) img.src = img.dataset.src;
        if (count) count.textContent = `${index + 1} / ${slides.length}`;
      }

      function setOrigin(slide, event) {
        const rect = slide.querySelector('img').getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 100;
        const y = ((event.clientY - rect.top) / rect.height) * 100;
        slide.style.setProperty('--zoom-x', `${Math.max(0, Math.min(100, x))}%`);
        slide.style.setProperty('--zoom-y', `${Math.max(0, Math.min(100, y))}%`);
      }

      root.querySelectorAll('[data-zoom-open]').forEach((button) => {
        button.addEventListener('click', () => {
          show(parseInt(button.dataset.zoomOpen, 10) || 0);
          dialog.showModal();
          document.documentElement.classList.add('zoom-open');
        });
      });

      slides.forEach((slide) => {
        const img = slide.querySelector('img');
        img.addEventListener('click', (event) => {
          setOrigin(slide, event);
          slide.classList.toggle('is-zoomed');
        });
        slide.addEventListener('pointermove', (event) => {
          if (event.pointerType === 'mouse' && slide.classList.contains('is-zoomed')) setOrigin(slide, event);
        });
      });

      // Swipe between photos on touch screens (only while not zoomed in)
      dialog.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse') startX = event.clientX;
      });
      dialog.addEventListener('pointerup', (event) => {
        if (startX === null) return;
        const dx = event.clientX - startX;
        startX = null;
        if (Math.abs(dx) > 50 && !slides[index].classList.contains('is-zoomed')) show(index + (dx < 0 ? 1 : -1));
      });

      const prev = dialog.querySelector('[data-zoom-prev]');
      const next = dialog.querySelector('[data-zoom-next]');
      if (prev) prev.addEventListener('click', () => show(index - 1));
      if (next) next.addEventListener('click', () => show(index + 1));
      dialog.querySelector('[data-zoom-close]').addEventListener('click', () => dialog.close());
      // a click on the dark backdrop (not the photo or a control) closes
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog || event.target.classList.contains('product-zoom__track') || event.target.matches('[data-zoom-slide]')) dialog.close();
      });
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') show(index - 1);
        if (event.key === 'ArrowRight') show(index + 1);
      });
      dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('zoom-open');
        // leave the page slider on the photo the customer ended on
        const opener = root.querySelector(`[data-zoom-open="${index}"]`);
        const slide = opener && opener.closest('.product__media');
        if (slider && slider.baaghiGoTo && slide) slider.baaghiGoTo(Array.from(slide.parentNode.children).indexOf(slide));
      });
    });
  }

  /* ── Sticky add to cart ───────────────────────────────────────────────── */

  function initStickyAtc() {
    const sticky = document.querySelector('[data-sticky-atc]');
    const trigger = document.querySelector('[data-sticky-trigger]');
    if (!sticky || !trigger || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => sticky.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0));
      },
      { threshold: 0 }
    );
    observer.observe(trigger);
  }

  /* ── Quick add (opens drawer with options when needed) ────────────────── */

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-quick-add-url]');
    if (!btn) return;
    event.preventDefault();
    const url = btn.getAttribute('data-quick-add-url');
    const drawer = document.getElementById('QuickAddDrawer');
    if (!drawer) {
      window.location.href = url;
      return;
    }
    const body = drawer.querySelector('[data-quick-add-body]');
    body.innerHTML = '<div class="quick-add__loading" style="display:grid;place-items:center;min-height:200px"><span class="spinner"></span></div>';
    window.BAAGHI.Drawer.open('QuickAddDrawer', btn);

    fetch(`${url}${url.indexOf('?') > -1 ? '&' : '?'}section_id=quick-add`)
      .then((res) => res.text())
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const markup = doc.querySelector('[data-quick-add-inner]');
        body.innerHTML = markup ? markup.innerHTML : '';
        initVariantPicker(body);
      })
      .catch(() => {
        window.location.href = url;
      });
  });

  /* ── Init ─────────────────────────────────────────────────────────────── */

  function init(scope) {
    initWhatsAppOrders(scope || document);
    initVariantPicker(scope || document);
    initSliders(scope || document);
    initZoom(scope || document);
  }

  init(document);
  initStickyAtc();

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
    initStickyAtc();
  });
})();
